import { isEqual, memoize, merge } from 'lodash';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { AcknowledgableSuggestion } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import {
  AcknowledgedSuggestion,
  SuggestionAcknowledgementContext,
} from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { debug } from '@/utils/diagnostics';

function useContextValue(): SuggestionAcknowledgementContext {
  debug('[DEBUG] SuggestionAcknowledgement - useContextValue called');

  // These are the suggestions that have previously been acknowledged. We won't
  // show them as suggestions, but will use the evidence to locate the suggestion
  // on the document (if accepted).
  const [
    previouslyAcknowledgedSuggestions,
    setPreviouslyAcknowledgedSuggestions,
  ] = useState<SuggestionAcknowledgementContext['acknowledgedSuggestions']>({});

  // These are the suggestions that have been acknowledged during the current
  // edit session. Once the form is submitted, these will be sent to the backend
  // to be acknowledged.
  const [acknowledgedSuggestions, setAcknowledgedSuggestions] = useState<
    SuggestionAcknowledgementContext['acknowledgedSuggestions']
  >({});

  const acknowledgeSuggestion = useCallback(
    (suggestion: AcknowledgedSuggestion) => {
      debug(
        '[DEBUG] SuggestionAcknowledgement - acknowledgeSuggestion called',
        { suggestionID: suggestion.suggestionID }
      );
      setAcknowledgedSuggestions((prev) => ({
        ...prev,
        [suggestion.suggestionID]: suggestion,
      }));
    },
    []
  );

  // Create a memoized version of the filter function
  const memoizedFilterSuggestions = useCallback(() => {
    return memoize(
      (
        suggestions: Record<string, AcknowledgedSuggestion>,
        matchedId: string
      ) => {
        debug(
          '[DEBUG] SuggestionAcknowledgement - memoizedFilterSuggestions called for',
          { matchedId }
        );
        return Object.values(suggestions).filter(
          (s) =>
            s.clientProfileId === matchedId ||
            s.clientOrganizationId === matchedId ||
            s.entityId === matchedId ||
            s.testamentaryEntityId === matchedId
        );
      },
      // Custom resolver function for memoize that creates a cache key from the matchedId and a hash of the suggestions
      (suggestions, matchedId) => {
        // Simple hash function for the suggestions object
        const suggestionsHash = Object.keys(suggestions).sort().join('|');
        return `${matchedId}:${suggestionsHash}`;
      }
    );
  }, []);

  // Use the memoized function
  const memoizedFilter = useMemo(
    () => memoizedFilterSuggestions(),
    [memoizedFilterSuggestions]
  );

  const getAcknowledgedSuggestion = useCallback(
    (matchedId: string) => {
      debug(
        '[DEBUG] SuggestionAcknowledgement - getAcknowledgedSuggestion called for',
        { matchedId }
      );
      return memoizedFilter(acknowledgedSuggestions, matchedId);
    },
    [acknowledgedSuggestions, memoizedFilter]
  );

  const initPreviouslyAcknowledgedSuggestions = useCallback(
    (suggestions: AcknowledgableSuggestion[]) => {
      debug(
        '[DEBUG] SuggestionAcknowledgement - initPreviouslyAcknowledgedSuggestions called with',
        { count: suggestions.length }
      );

      function mapAckedSuggestion(
        suggestion: AcknowledgableSuggestion
      ): AcknowledgedSuggestion | null {
        if (!suggestion.acceptanceStatus) return null;

        return {
          status: suggestion.acceptanceStatus,
          suggestionID: suggestion.id,
          clientProfileId: suggestion.clientProfile?.id,
          clientOrganizationId: suggestion.clientOrganization?.id,
          entityId: suggestion.entity?.id,
          testamentaryEntityId: suggestion.testamentaryEntity?.id,
        };
      }

      const acked = suggestions.reduce(
        (acc, s) => {
          const ackedSuggestion = mapAckedSuggestion(s);
          if (ackedSuggestion) {
            acc[s.id] = ackedSuggestion;
          }
          return acc;
        },
        {} as SuggestionAcknowledgementContext['acknowledgedSuggestions']
      );

      debug(
        '[DEBUG] SuggestionAcknowledgement - setting previously acknowledged suggestions',
        { count: Object.keys(acked).length }
      );

      // Only update state if there are actual changes to prevent unnecessary re-renders
      setPreviouslyAcknowledgedSuggestions((prev) => {
        // If there are no new suggestions to add, return the previous state
        if (Object.keys(acked).length === 0) {
          return prev;
        }

        // Create the merged result
        const mergedResult = merge({}, prev, acked);

        // Only update if there's an actual change (deep equality check)
        if (isEqual(prev, mergedResult)) {
          return prev; // Return the previous state reference if no changes
        }

        return mergedResult;
      });

      setAcknowledgedSuggestions((prev) => {
        // If there are no new suggestions to add, return the previous state
        if (Object.keys(acked).length === 0) {
          return prev;
        }

        // Create the merged result
        const mergedResult = merge({}, prev, acked);

        // Only update if there's an actual change (deep equality check)
        if (isEqual(prev, mergedResult)) {
          return prev; // Return the previous state reference if no changes
        }

        return mergedResult;
      });
    },
    []
  );

  const resetAcknowledgedSuggestions = useCallback(() => {
    debug(
      '[DEBUG] SuggestionAcknowledgement - resetAcknowledgedSuggestions called'
    );
    setAcknowledgedSuggestions({ ...previouslyAcknowledgedSuggestions });
  }, [previouslyAcknowledgedSuggestions]);

  // Add effect to track state changes
  useEffect(() => {
    debug(
      '[DEBUG] SuggestionAcknowledgement - acknowledgedSuggestions changed',
      { count: Object.keys(acknowledgedSuggestions).length }
    );
  }, [acknowledgedSuggestions]);

  useEffect(() => {
    debug(
      '[DEBUG] SuggestionAcknowledgement - previouslyAcknowledgedSuggestions changed',
      { count: Object.keys(previouslyAcknowledgedSuggestions).length }
    );
  }, [previouslyAcknowledgedSuggestions]);

  return useMemo(
    () => ({
      acknowledgeSuggestion,
      acknowledgedSuggestions,
      getAcknowledgedSuggestion,
      previouslyAcknowledgedSuggestions,
      initPreviouslyAcknowledgedSuggestions,
      resetAcknowledgedSuggestions,
    }),
    [
      acknowledgeSuggestion,
      acknowledgedSuggestions,
      getAcknowledgedSuggestion,
      previouslyAcknowledgedSuggestions,
      initPreviouslyAcknowledgedSuggestions,
      resetAcknowledgedSuggestions,
    ]
  );
}

export function SuggestionAcknowledgementProvider({
  children,
}: PropsWithChildren<unknown>) {
  const value = useContextValue();

  return (
    <SuggestionAcknowledgementContext.Provider value={value}>
      {children}
    </SuggestionAcknowledgementContext.Provider>
  );
}
